import React from 'react';
import { Alert, AlertType } from 'components/lds';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  UpdateCutoffUnitEnum,
  UpdateFeatureProperties,
  UpdateFeatureRule,
} from 'application-types';
import useFlag from 'lane-shared/hooks/useFlag';
import { FeatureFlag } from 'constants-flags';

import Label from 'components/general/Label';
import RadioGroup from 'components/form/RadioGroup';
import Toggle from 'components/form/Toggle';
import { FeatureRendererPropsType } from 'components/renderers/features/FeatureRendererPropsType';
import ReservableFeatureSlider from 'components/renderers/features/Reservable/ReservableFeatureSlider';

import styles from './styles.scss';

const CUTOFF_VALUE_MIN = 1;
const CUTOFF_VALUE_MAX = 120;

const DEFAULT_RULE: UpdateFeatureRule = {
  allGroupRoles: true,
  canUpdateAnytime: true,
  groupRoles: [],
  cutoffUnit: UpdateCutoffUnitEnum.Hour,
  cutoffValue: 1,
};

export function UpdateFeatureRenderer({
  className,
  style,
  feature,
  contentFeature,
  onFeatureUpdated,
}: FeatureRendererPropsType<UpdateFeatureProperties>) {
  const { t } = useTranslation();

  const isMemberInteractionUpdateEnabled = useFlag(
    FeatureFlag.MemberInteractionUpdate,
    false
  );

  const settings = contentFeature?.feature;
  const rule = settings?.rules?.[0] ?? DEFAULT_RULE;

  // defensive fallback: the admin picker (FeatureBuilder) already hides
  // this feature when the flag is off, this just guards direct URL access.
  if (!isMemberInteractionUpdateEnabled) {
    return React.createElement(React.Fragment);
  }

  function updateRule(updates: Partial<UpdateFeatureRule>) {
    onFeatureUpdated({ rules: [{ ...rule, ...updates }] });
  }

  const cutoffUnitValues: string[] = Object.values(UpdateCutoffUnitEnum);

  function isUpdateCutoffUnit(value: string): value is UpdateCutoffUnitEnum {
    return cutoffUnitValues.includes(value);
  }

  const cutoffUnitOptions = Object.values(UpdateCutoffUnitEnum).map(unit => ({
    id: unit,
    text: `web.content.feature.update.rules.cutoffUnit.options.${unit}`,
  }));

  return (
    <section
      className={cx(styles.Update, className)}
      style={style}
      data-test="updateFeaturePanel"
    >
      <Label h1>{t(feature.friendlyName || '')}</Label>
      <p data-test="updateFeatureDescription">{t(feature.description)}</p>
      <Alert type={AlertType.info}>
        {t('web.content.feature.update.info')}
      </Alert>

      <div
        className={styles.toggleField}
        data-test="updateCanUpdateAnytimeField"
      >
        <Toggle
          testId="updateCanUpdateAnytimeToggle"
          valuetestId="updateCanUpdateAnytimeToggleValue"
          value={rule.canUpdateAnytime}
          onChange={canUpdateAnytime => updateRule({ canUpdateAnytime })}
          text={t('web.content.feature.update.rules.canUpdateAnytime.friendlyName')}
          description= {t('web.content.feature.update.rules.canUpdateAnytime.description')}
          TooltipComponent={t(
            'web.content.feature.update.rules.canUpdateAnytime.info'
          )}
        />
      </div>

      {!rule.canUpdateAnytime && (
        <fieldset className={styles.cutoff} data-test="updateCutoffSection">
          <Label
            className={styles.fieldLabel}
            TooltipComponent={t(
              'web.content.feature.update.rules.cutoffUnit.description'
            )}
          >
            {t('web.content.feature.update.rules.cutoffUnit.friendlyName')}
          </Label>
          <RadioGroup
            name="update-cutoff-unit"
            schema={{ id: 'id', text: 'text' }}
            items={cutoffUnitOptions}
            selected={rule.cutoffUnit}
            onChange={(cutoffUnit: string) => {
              if (isUpdateCutoffUnit(cutoffUnit)) {
                updateRule({ cutoffUnit });
              }
            }}
          />
          <ReservableFeatureSlider
            label={t('web.content.feature.update.rules.cutoffValue.friendlyName')}
            TooltipComponent={t(
              'web.content.feature.update.rules.cutoffValue.info'
            )}
            boldLabel={false}
            value={rule.cutoffValue}
            min={CUTOFF_VALUE_MIN}
            max={CUTOFF_VALUE_MAX}
            summary={t(
              `web.content.feature.update.rules.cutoffValue.summary.${rule.cutoffUnit}`,
              { count: rule.cutoffValue }
            )}
            onChange={cutoffValue => updateRule({ cutoffValue })}
          />
        </fieldset>
      )}
    </section>
  );
}
